import { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

/**
 * #37 (ESERV2-1441) — the branch one review line will be ordered from.
 *
 * One line = one branch. The options are the company's favorite branches (the
 * same "my branches" set search and stock checks use) plus "Vendor default",
 * which clears the override and lets the send resolve the vendor's branch as
 * before. When lines of one vendor end up on different branches, the send
 * places one Heritage order per branch.
 *
 * The visible selection only changes on the server's confirmation — showing a
 * branch the line does not actually have would route real goods wrong.
 */
const LineBranchSelector = ({ item, favoriteBranches, branchStockByPart, partNumber, onChanged }) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.review.LineBranchSelector";
  const tr = (key) => t(`${tBase}.${key}`);
  const [saving, setSaving] = useState(false);

  const stockFor = (branchCode) => {
    if (!partNumber || !branchCode) return null;
    const readings = branchStockByPart?.[partNumber];
    const entry = readings?.find((row) => row.branch_code === branchCode);
    return entry?.qty ?? null;
  };

  const save = async (branchId) => {
    setSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/updateLineBranch`,
        { cartItemId: item.id, itemId: item.item_id, branchId }
      );
      if (onChanged) await onChanged();
    } catch (error) {
      const message = axios.isAxiosError?.(error)
        ? error.response?.data?.message
        : null;
      toast.error(message || tr("Could not save the branch"));
    } finally {
      setSaving(false);
    }
  };

  const selectedQty = stockFor(item.line_branch_code);

  return (
    <div className="line-branch-selector">
      <div className="review-detail-text">{tr("Order from branch")}</div>
      <select
        className="line-branch-select"
        value={item.line_branch_id != null ? String(item.line_branch_id) : ""}
        disabled={saving}
        aria-label={tr("Order from branch")}
        onChange={(e) => save(e.target.value === "" ? null : Number(e.target.value))}
      >
        <option value="">{tr("Vendor default")}</option>
        {(favoriteBranches || []).map((branch) => {
          const qty = stockFor(branch.branch_code);
          return (
            <option key={branch.id} value={String(branch.id)}>
              {branch.branch_name || branch.branch_code}
              {qty != null ? ` — ${qty} ${tr("in stock")}` : ""}
            </option>
          );
        })}
      </select>
      {item.line_branch_id != null && (
        <div className="line-branch-stock-hint">
          {item.line_branch_label || item.line_branch_code}
          {selectedQty != null ? ` — ${selectedQty} ${tr("in stock")}` : ""}
        </div>
      )}
    </div>
  );
};

export default LineBranchSelector;
